// Shared Zod helpers for Target + Strategic submit rules (min≤max, numeric, source caps).
import { z } from 'zod';
import { SOURCE_MAX_CAPS } from '@/features/research/composer/sourceCaps';

export const optionalNumeric = z
  .string()
  .optional()
  .refine((s) => {
    const t = (s ?? '').trim();
    return !t || Number.isFinite(Number(t));
  }, 'Enter a valid number');

export function parseNum(raw: string | undefined): number | undefined {
  const t = (raw ?? '').trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

export function addMaxResultsIssue(
  ctx: z.RefinementCtx,
  enabled: boolean,
  raw: string | undefined,
  cap: number,
  path: (string | number)[],
  label: string,
): void {
  if (!enabled) return;
  const n = Number.parseInt((raw ?? '').trim(), 10);
  if (!Number.isFinite(n) || n < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message: `${label} max results must be at least 1.`,
    });
    return;
  }
  if (n > cap) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message: `${label} max results cannot exceed ${cap}.`,
    });
  }
}

export type SizeRangeFields = {
  minRevenue?: string;
  maxRevenue?: string;
  minEmployees?: string;
  maxEmployees?: string;
};

export function addSizeRangeIssues(ctx: z.RefinementCtx, size: SizeRangeFields): void {
  const minRev = parseNum(size.minRevenue);
  const maxRev = parseNum(size.maxRevenue);
  if (minRev != null && maxRev != null && minRev > maxRev) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['size', 'maxRevenue'],
      message: 'Max revenue must be greater than or equal to min revenue.',
    });
  }

  const minEmp = parseNum(size.minEmployees);
  const maxEmp = parseNum(size.maxEmployees);
  if (minEmp != null && maxEmp != null && minEmp > maxEmp) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['size', 'maxEmployees'],
      message: 'Max employees must be greater than or equal to min employees.',
    });
  }
}

export type AdditionalSearchFields = {
  enableApolloSearch: boolean;
  apolloMaxResults?: string;
  enableGmapsSearch: boolean;
  gmapsMaxResults?: string;
  enableCoresignalSearch: boolean;
  coresignalMaxResults?: string;
  enableFindallSearch: boolean;
  findallMaxResults?: string;
  enableDatamagnetSearch: boolean;
  datamagnetLive: boolean;
  datamagnetDatabase: boolean;
  datamagnetLiveMaxResults?: string;
  datamagnetDbMaxResults?: string;
};

export function addSourceCapIssues(ctx: z.RefinementCtx, a: AdditionalSearchFields): void {
  addMaxResultsIssue(
    ctx,
    a.enableApolloSearch,
    a.apolloMaxResults,
    SOURCE_MAX_CAPS.apollo,
    ['additionalSearch', 'apolloMaxResults'],
    'Apollo',
  );
  addMaxResultsIssue(
    ctx,
    a.enableGmapsSearch,
    a.gmapsMaxResults,
    SOURCE_MAX_CAPS.gmaps,
    ['additionalSearch', 'gmapsMaxResults'],
    'Google Maps',
  );
  addMaxResultsIssue(
    ctx,
    a.enableCoresignalSearch,
    a.coresignalMaxResults,
    SOURCE_MAX_CAPS.coresignal,
    ['additionalSearch', 'coresignalMaxResults'],
    'Coresignal',
  );
  addMaxResultsIssue(
    ctx,
    a.enableFindallSearch,
    a.findallMaxResults,
    SOURCE_MAX_CAPS.findall,
    ['additionalSearch', 'findallMaxResults'],
    'FindAll',
  );
  addMaxResultsIssue(
    ctx,
    a.enableDatamagnetSearch && a.datamagnetLive,
    a.datamagnetLiveMaxResults,
    SOURCE_MAX_CAPS.datamagnet,
    ['additionalSearch', 'datamagnetLiveMaxResults'],
    'DataMagnet live search',
  );
  addMaxResultsIssue(
    ctx,
    a.enableDatamagnetSearch && a.datamagnetDatabase,
    a.datamagnetDbMaxResults,
    SOURCE_MAX_CAPS.datamagnet,
    ['additionalSearch', 'datamagnetDbMaxResults'],
    'DataMagnet database search',
  );
  if (a.enableDatamagnetSearch && !a.datamagnetLive && !a.datamagnetDatabase) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['additionalSearch', 'datamagnetLive'],
      message: 'Choose live search, database search, or both for DataMagnet.',
    });
  }
}

/** Append the DataMagnet source fields to a research submission (Target + Strategic). */
export function appendDatamagnetFields(
  fd: FormData,
  a: {
    enableDatamagnetSearch?: boolean;
    datamagnetLive?: boolean;
    datamagnetDatabase?: boolean;
    datamagnetLiveMaxResults?: string;
    datamagnetDbMaxResults?: string;
    datamagnetFunding?: boolean;
  },
): void {
  if (!a.enableDatamagnetSearch) return;
  const mode = datamagnetSearchMode(a);
  fd.append('enable_datamagnet_search', 'true');
  fd.append('datamagnet_search_mode', mode);
  // One count per search type; only the searches that run send theirs.
  if (mode !== 'database')
    fd.append('datamagnet_live_max_results', a.datamagnetLiveMaxResults ?? '');
  if (mode !== 'live') fd.append('datamagnet_db_max_results', a.datamagnetDbMaxResults ?? '');
  if (a.datamagnetFunding) fd.append('datamagnet_funding', 'true');
}

/** The single `datamagnet_search_mode` the backend takes for the two checkboxes. */
export function datamagnetSearchMode(a: {
  datamagnetLive?: boolean;
  datamagnetDatabase?: boolean;
}): 'live' | 'database' | 'both' {
  if (a.datamagnetLive && !a.datamagnetDatabase) return 'live';
  if (a.datamagnetDatabase && !a.datamagnetLive) return 'database';
  return 'both';
}

/** Flatten RHF / Zod error objects into the first human message. */
export function firstErrorMessage(errors: unknown, fallback: string): string {
  const skip = new Set(['ref', 'type', 'types']);
  const walk = (node: unknown, path: string[]): string | undefined => {
    if (!node || typeof node !== 'object') return undefined;
    const rec = node as { message?: unknown; _errors?: unknown };
    if (typeof rec.message === 'string' && rec.message.trim()) {
      if (rec.message === 'Required' && path.length) return `${path.join('.')} is required.`;
      return rec.message;
    }
    if (Array.isArray(rec._errors)) {
      const first = rec._errors.find((m) => typeof m === 'string' && m.trim());
      if (typeof first === 'string') {
        return first === 'Required' && path.length ? `${path.join('.')} is required.` : first;
      }
    }
    for (const [key, value] of Object.entries(rec)) {
      if (skip.has(key)) continue;
      const found = walk(value, [...path, key]);
      if (found) return found;
    }
    return undefined;
  };
  return walk(errors, []) ?? fallback;
}
